import React, { useState, useMemo } from "react";
import {
  Box,
  Tabs,
  Tab,
  Chip,
  Card,
  LinearProgress,
  Backdrop,
} from "@mui/material";


import { arSD } from "@mui/x-data-grid/locales"; // For DataGrid localization
import { useGetAccountsTreeQuery } from "../../../redux/Slices/financial-accountsApi";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import RTLDataGrid from "components/styled-component/RTLDataGrid";



// create the Accounts Tree
const AccountsTree = () => {
  const [selectedTab, setSelectedTab] = useState("all");
  const { data: accountsTreeData, isLoading, isError } = useGetAccountsTreeQuery();
  // Categorize accounts by type
  const categorizedAccounts = useMemo(() => {
    const categories = {
      all: accountsTreeData?.data || [],
      asset: [],
      liability: [],
      equity: [],
      revenue: [],
      expense: [],
    };

    accountsTreeData?.data?.forEach((account) => {
      if (categories[account.type]) {
        categories[account.type].push(account);
      }
    });

    return categories;
  }, [accountsTreeData]);

  // Get category counts
  const categoryCounts = useMemo(
    () => ({
      all: accountsTreeData?.data.length,
      asset: categorizedAccounts.asset.length,
      liability: categorizedAccounts.liability.length,
      equity: categorizedAccounts.equity.length,
      revenue: categorizedAccounts.revenue.length,
      expense: categorizedAccounts.expense.length,
    }),
    [categorizedAccounts, accountsTreeData]
  );

  // DataGrid columns
  const columns = [
    {
      field: "type_name",
      headerName: "النوع",
      width: 150,

      renderCell: (params) => {
        const colors = {
          asset: "success",
          liability: "error",
          equity: "info",
          revenue: "primary",
          expense: "warning",
        };
        return (
          <Chip
            label={params.value}
            color={colors[params.row.type] || "default"}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        );
      },
    },
    {
      field: "code",
      headerName: "الكود",
      width: 120,
    },
    {
      field: "name",
      headerName: "اسم الحساب",
      width: 200,
      flex: 1,
    },

    {
      field: "parent_name",
      headerName: "الحساب الأب",
      width: 150,

      valueGetter: (params) => params || "-",
    },
    {
      field: "balance",
      headerName: "الرصيد",
      width: 130,

      valueFormatter: (params) => {
        return new Intl.NumberFormat("ar-SD", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(params);
      },
    },
    {
      field: "current_balance",
      headerName: "الرصيد الحالي",
      width: 130,

      valueFormatter: (params) => {
        return new Intl.NumberFormat("ar-SD", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(params);
      },
    },
    {
      field: "is_active",
      headerName: "الحالة",
      width: 100,

      renderCell: (params) => (
        <Chip
          label={params.value ? "نشط" : "غير نشط"}
          color={params.value ? "success" : "default"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "is_parent",
      headerName: "حساب رئيسي",
      width: 120,

      type: "boolean",
      renderCell: (params) => (params.value ? "✓" : "-"),
    },
  ];

  const tabs = [
    { value: "all", label: "الكل", color: "secondary" },
    { value: "asset", label: "الأصول", color: "success" },
    { value: "liability", label: "الخصوم", color: "error" },
    { value: "equity", label: "حقوق الملكية", color: "info" },
    { value: "revenue", label: "الإيرادات", color: "primary" },
    { value: "expense", label: "المصروفات", color: "warning" },
  ];

if(isLoading) return (
 <Backdrop>
   <LinearProgress color="primary" />
</Backdrop>);

  return (
    <MDBox pt={6} pb={3}>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={3}
          px={2}
          borderRadius="lg"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            background: "linear-gradient(90deg, #ca9937, #e0b15a)",
            boxShadow: 3,
          }}
        >
          <MDTypography variant="h6" color="white">
            قائمة الحسابات
          </MDTypography>
        </MDBox>
        <Box sx={{ width: "100%", height: "100vh", p: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
            <Tabs
              value={selectedTab}
              onChange={(e, newValue) => setSelectedTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& .MuiTab-root": {
                  fontWeight: 600,
                  fontSize: "1rem",
                },
              }}
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {tab.label}
                      <Chip label={categoryCounts[tab.value]} size="small" color={tab.color} />
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ flexGrow: 1, width: "100%" }}>
            <RTLDataGrid
              rows={categorizedAccounts[selectedTab]}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 25, 50]}
              disableSelectionOnClick
              localeText={arSD.components.MuiDataGrid.defaultProps.localeText}
              isActions={false}
             
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10, page: 0 },
                },
              }}
            />
          </Box>
        </Box>
      </Card>
    </MDBox>
  );
};

export default AccountsTree;
